import { memo, useCallback, useEffect, useRef, useState } from "react";
import * as styles from "./dither-background.module.css";

/* cspell:disable -- GLSL shader code */
const VERTEX_SHADER = `
precision highp float;
attribute vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `
precision highp float;
uniform vec2 resolution;
uniform float time;
uniform float waveSpeed;
uniform float waveFrequency;
uniform float waveAmplitude;
uniform vec3 waveColor;
uniform vec3 backgroundColor;
uniform vec2 mousePos;
uniform int enableMouseInteraction;
uniform float mouseRadius;
uniform float colorNum;
uniform float pixelSize;
uniform sampler2D bayerTexture;

vec4 mod289(vec4 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
vec2 fade(vec2 t) { return t*t*t*(t*(t*6.0-15.0)+10.0); }

float cnoise(vec2 P) {
  vec4 Pi = floor(P.xyxy) + vec4(0.0,0.0,1.0,1.0);
  vec4 Pf = fract(P.xyxy) - vec4(0.0,0.0,1.0,1.0);
  Pi = mod289(Pi);
  vec4 ix = Pi.xzxz;
  vec4 iy = Pi.yyww;
  vec4 fx = Pf.xzxz;
  vec4 fy = Pf.yyww;
  vec4 i = permute(permute(ix) + iy);
  vec4 gx = fract(i * (1.0/41.0)) * 2.0 - 1.0;
  vec4 gy = abs(gx) - 0.5;
  vec4 tx = floor(gx + 0.5);
  gx = gx - tx;
  vec2 g00 = vec2(gx.x, gy.x);
  vec2 g10 = vec2(gx.y, gy.y);
  vec2 g01 = vec2(gx.z, gy.z);
  vec2 g11 = vec2(gx.w, gy.w);
  vec4 norm = taylorInvSqrt(vec4(dot(g00,g00), dot(g01,g01), dot(g10,g10), dot(g11,g11)));
  g00 *= norm.x; g01 *= norm.y; g10 *= norm.z; g11 *= norm.w;
  float n00 = dot(g00, vec2(fx.x, fy.x));
  float n10 = dot(g10, vec2(fx.y, fy.y));
  float n01 = dot(g01, vec2(fx.z, fy.z));
  float n11 = dot(g11, vec2(fx.w, fy.w));
  vec2 fade_xy = fade(Pf.xy);
  vec2 n_x = mix(vec2(n00, n01), vec2(n10, n11), fade_xy.x);
  return 2.3 * mix(n_x.x, n_x.y, fade_xy.y);
}

const int OCTAVES = 4;
float fbm(vec2 p) {
  float value = 0.0;
  float amp = 1.0;
  float freq = waveFrequency;
  for (int i = 0; i < OCTAVES; i++) {
    value += amp * abs(cnoise(p));
    p *= freq;
    amp *= waveAmplitude;
  }
  return value;
}

float pattern(vec2 p) {
  vec2 p2 = p - time * waveSpeed;
  return fbm(p + fbm(p2));
}

vec3 dither(vec2 uv, vec3 color) {
  vec2 coord = uv * resolution / pixelSize;
  vec2 bayerUV = coord / 8.0;
  float threshold = texture2D(bayerTexture, bayerUV).r - 0.25;
  float step = 1.0 / (colorNum - 1.0);
  color += threshold * step;
  return floor(color * (colorNum - 1.0) + 0.5) / (colorNum - 1.0);
}

void main() {
  vec2 uv = gl_FragCoord.xy / resolution.xy;
  vec2 normalizedPixelSize = pixelSize / resolution;
  vec2 pixelatedUV = normalizedPixelSize * floor(uv / normalizedPixelSize);

  vec2 waveUv = pixelatedUV;
  waveUv -= 0.5;
  waveUv.x *= resolution.x / resolution.y;

  float f = pattern(waveUv);

  if (enableMouseInteraction == 1) {
    vec2 mouseNDC = (mousePos / resolution - 0.5) * vec2(1.0, -1.0);
    mouseNDC.x *= resolution.x / resolution.y;
    float dist = length(waveUv - mouseNDC);
    float effect = 1.0 - smoothstep(0.0, mouseRadius, dist);
    f -= 0.5 * effect;
  }

  vec3 col = mix(backgroundColor, waveColor, f);
  col = dither(uv, col);
  gl_FragColor = vec4(col, 1.0);
}
`;
/* cspell:enable */

// 8x8 Bayer matrix for ordered dithering
const BAYER_MATRIX = [
  0, 48, 12, 60, 3, 51, 15, 63, 32, 16, 44, 28, 35, 19, 47, 31, 8, 56, 4, 52,
  11, 59, 7, 55, 40, 24, 36, 20, 43, 27, 39, 23, 2, 50, 14, 62, 1, 49, 13, 61,
  34, 18, 46, 30, 33, 17, 45, 29, 10, 58, 6, 54, 9, 57, 5, 53, 42, 26, 38, 22,
  41, 25, 37, 21,
];

function createBayerTexture(gl: WebGLRenderingContext): WebGLTexture | null {
  const texture = gl.createTexture();
  if (!texture) {
    return null;
  }

  const data = new Uint8Array(64 * 4);
  for (let i = 0; i < 64; i++) {
    const val = Math.floor((BAYER_MATRIX[i]! / 64) * 255);
    data[i * 4] = val;
    data[i * 4 + 1] = val;
    data[i * 4 + 2] = val;
    data[i * 4 + 3] = 255;
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    8,
    8,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    data,
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);

  return texture;
}

function compileShader(
  gl: WebGLRenderingContext,
  source: string,
  type: number,
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) {
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createProgram(gl: WebGLRenderingContext): WebGLProgram | null {
  const vs = compileShader(gl, VERTEX_SHADER, gl.VERTEX_SHADER);
  const fs = compileShader(gl, FRAGMENT_SHADER, gl.FRAGMENT_SHADER);
  if (!vs || !fs) {
    return null;
  }

  const program = gl.createProgram();
  if (!program) {
    return null;
  }
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
}

export interface DitherBackgroundProps {
  waveSpeed?: number;
  waveFrequency?: number;
  waveAmplitude?: number;
  waveColor?: [number, number, number];
  backgroundColor?: [number, number, number];
  colorNum?: number;
  pixelSize?: number;
  disableAnimation?: boolean;
  enableMouseInteraction?: boolean;
  mouseRadius?: number;
  opacity?: number;
}

export const DitherBackground = memo(function DitherBackground({
  waveSpeed = 0.015,
  waveFrequency = 2,
  waveAmplitude = 0.3,
  waveColor = [0.75, 0.75, 0.75],
  backgroundColor = [0.98, 0.98, 0.98],
  colorNum: rawColorNum = 4,
  pixelSize = 1,
  disableAnimation = false,
  enableMouseInteraction = true,
  mouseRadius = 0.1,
  opacity = 0.66,
}: DitherBackgroundProps) {
  const colorNum = Math.max(2, rawColorNum);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const rafRef = useRef<number>(0);
  const mouseRef = useRef({ x: 0, y: 0 });
  const startTimeRef = useRef(0);
  const [hasError, setHasError] = useState(false);

  const propsRef = useRef({
    waveSpeed,
    waveFrequency,
    waveAmplitude,
    waveColor,
    backgroundColor,
    colorNum,
    pixelSize,
    disableAnimation,
    enableMouseInteraction,
    mouseRadius,
  });
  propsRef.current = {
    waveSpeed,
    waveFrequency,
    waveAmplitude,
    waveColor,
    backgroundColor,
    colorNum,
    pixelSize,
    disableAnimation,
    enableMouseInteraction,
    mouseRadius,
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const rect = canvas.getBoundingClientRect();
    mouseRef.current.x = e.clientX - rect.left;
    mouseRef.current.y = e.clientY - rect.top;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const gl = canvas.getContext("webgl", {
      antialias: false,
      preserveDrawingBuffer: false,
      failIfMajorPerformanceCaveat: true,
    });

    if (!gl) {
      setHasError(true);
      return;
    }

    const program = createProgram(gl);
    if (!program) {
      setHasError(true);
      return;
    }

    const bayerTexture = createBayerTexture(gl);
    if (!bayerTexture) {
      setHasError(true);
      return;
    }

    glRef.current = gl;
    programRef.current = program;

    // Full-screen quad
    const buffer = gl.createBuffer();
    if (!buffer) {
      setHasError(true);
      return;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    const posAttr = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(posAttr);
    gl.vertexAttribPointer(posAttr, 2, gl.FLOAT, false, 0, 0);

    gl.useProgram(program);

    // Bind bayer texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, bayerTexture);
    const bayerLoc = gl.getUniformLocation(program, "bayerTexture");
    gl.uniform1i(bayerLoc, 0);

    // Cache uniform locations
    const uniforms = {
      resolution: gl.getUniformLocation(program, "resolution"),
      time: gl.getUniformLocation(program, "time"),
      waveSpeed: gl.getUniformLocation(program, "waveSpeed"),
      waveFrequency: gl.getUniformLocation(program, "waveFrequency"),
      waveAmplitude: gl.getUniformLocation(program, "waveAmplitude"),
      waveColor: gl.getUniformLocation(program, "waveColor"),
      backgroundColor: gl.getUniformLocation(program, "backgroundColor"),
      mousePos: gl.getUniformLocation(program, "mousePos"),
      enableMouseInteraction: gl.getUniformLocation(
        program,
        "enableMouseInteraction",
      ),
      mouseRadius: gl.getUniformLocation(program, "mouseRadius"),
      colorNum: gl.getUniformLocation(program, "colorNum"),
      pixelSize: gl.getUniformLocation(program, "pixelSize"),
    };

    startTimeRef.current = performance.now();

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const render = () => {
      resize();

      const p = propsRef.current;
      const elapsed = p.disableAnimation
        ? 0
        : (performance.now() - startTimeRef.current) / 1000;

      gl.uniform2f(uniforms.resolution, canvas.width, canvas.height);
      gl.uniform1f(uniforms.time, elapsed);
      gl.uniform1f(uniforms.waveSpeed, p.waveSpeed);
      gl.uniform1f(uniforms.waveFrequency, p.waveFrequency);
      gl.uniform1f(uniforms.waveAmplitude, p.waveAmplitude);
      gl.uniform3f(
        uniforms.waveColor,
        p.waveColor[0],
        p.waveColor[1],
        p.waveColor[2],
      );
      gl.uniform3f(
        uniforms.backgroundColor,
        p.backgroundColor[0],
        p.backgroundColor[1],
        p.backgroundColor[2],
      );
      gl.uniform2f(uniforms.mousePos, mouseRef.current.x, mouseRef.current.y);
      gl.uniform1i(
        uniforms.enableMouseInteraction,
        p.enableMouseInteraction ? 1 : 0,
      );
      gl.uniform1f(uniforms.mouseRadius, p.mouseRadius);
      gl.uniform1f(uniforms.colorNum, p.colorNum);
      gl.uniform1f(uniforms.pixelSize, p.pixelSize);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafRef.current = requestAnimationFrame(render);
    };

    render();

    window.addEventListener("mousemove", handleMouseMove);

    canvas.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      cancelAnimationFrame(rafRef.current);
      setHasError(true);
    });

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", handleMouseMove);
      gl.deleteProgram(program);
      gl.deleteTexture(bayerTexture);
      gl.deleteBuffer(buffer);
    };
  }, [handleMouseMove]);

  if (hasError) {
    return (
      <div className={styles.container}>
        <div className={styles.fallback} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <canvas className={styles.canvas} ref={canvasRef} style={{ opacity }} />
    </div>
  );
});
